import React from 'react';
import type { TimestampLike } from '../../types/dateLike';
import useDateFormatting, { DateFormatType } from '../../hooks/useDateFormatting';
import { Clock, Info } from 'lucide-react';

interface DateTimeDisplayProps {
  /** La fecha a mostrar (puede ser Date, TimestampLike o null/undefined) */
  date: Date | TimestampLike | null | undefined;
  
  /** Tipo de formato a utilizar */
  format?: DateFormatType;
  
  /** Si se debe usar la zona horaria del negocio (true) o del usuario (false) */
  useBusinessTZ?: boolean;
  
  /** Mostrar información sobre diferencia de zona horaria si es relevante */
  showTimeZoneInfo?: boolean;
  
  /** Mostrar ambas zonas horarias si son diferentes */
  showBothTimeZones?: boolean;
  
  /** Clase CSS adicional para el contenedor principal */
  className?: string;
  
  /** Zona horaria personalizada (sobreescribe useBusinessTZ) */
  customTimeZone?: string;
  
  /** Mostrar un ícono de reloj junto a la hora */
  showIcon?: boolean;
  
  /** Estilo compacto (menos espacio, fuente más pequeña) */
  compact?: boolean;
}

/**
 * Componente para mostrar fechas y horas con manejo automático de zonas horarias
 * 
 * Este componente utiliza el hook useDateFormatting para formatear fechas
 * considerando zonas horarias y mostrar información contextual cuando es necesario.
 */
const DateTimeDisplay: React.FC<DateTimeDisplayProps> = ({
  date,
  format = 'full',
  useBusinessTZ = true,
  showTimeZoneInfo = false,
  showBothTimeZones = false,
  className = '',
  customTimeZone,
  showIcon = false,
  compact = false
}) => {
  // Usar el hook de formateo de fechas
  const { 
    formatDate,
    formatDateWithZoneInfo,
    timeZoneInfo
  } = useDateFormatting({
    showBothTimeZones
  });
  
  // Si no hay fecha, mostrar un placeholder
  if (!date) {
    return (
      <span className={`text-content-3 ${className}`}>
        {showIcon && <Clock className="inline-block mr-1 h-3 w-3" />}
        N/A
      </span>
    );
  }
  
  // Si se especifica una zona horaria personalizada, usarla directamente
  if (customTimeZone) {
    return (
      <span className={className}>
        {showIcon && <Clock className="inline-block mr-1 h-3 w-3" />}
        {formatDate(date, { type: format, customTZ: customTimeZone })}
      </span>
    );
  }
  
  // Si no se necesita mostrar información de zona horaria o ambas zonas
  if (!showTimeZoneInfo && !showBothTimeZones) {
    return (
      <span className={className}>
        {showIcon && <Clock className="inline-block mr-1 h-3 w-3" />}
        {formatDate(date, { type: format, useBusinessTZ })}
      </span>
    );
  }
  
  // Obtener información de formato con posible zona horaria secundaria
  const dateInfo = formatDateWithZoneInfo(date, {
    type: format,
    alwaysShowBoth: showBothTimeZones
  });
  
  // Para formato compacto (mostrar en una sola línea con tooltip)
  if (compact && dateInfo.secondary) {
    return (
      <div className={`relative inline-block ${className}`}>
        <span className="group">
          {showIcon && <Clock className="inline-block mr-1 h-3 w-3" />}
          {dateInfo.text}
          {timeZoneInfo.isDifferent && (
            <Info className="inline-block ml-1 h-3 w-3 text-blue-500 cursor-help" />
          )}
          
          {/* Tooltip con información detallada */}
          <div className="absolute z-10 hidden group-hover:block bg-content text-surface text-xs rounded p-2 mt-1 min-w-[200px] shadow-lg">
            <div className="font-medium mb-1">{timeZoneInfo.business.label}:</div>
            <div>{dateInfo.text}</div>
            <div className="font-medium mt-2 mb-1">{timeZoneInfo.user.label}:</div>
            <div>{dateInfo.secondary}</div>
            {dateInfo.diff && (
              <div className="mt-2 pt-2 border-t border-surface/30 text-surface/70">
                {dateInfo.diff}
              </div>
            )}
          </div>
        </span>
      </div>
    );
  }
  
  // Versión extendida con toda la información
  return (
    <div className={`${className} ${compact ? 'text-sm' : ''}`}>
      <div className="flex items-center">
        {showIcon && <Clock className="inline-block mr-1 h-4 w-4" />}
        <span>{dateInfo.text}</span>
        
        {timeZoneInfo.isDifferent && showTimeZoneInfo && (
          <span className="ml-1 text-xs text-content-3 bg-canvas px-1 py-0.5 rounded">
            {timeZoneInfo.business.zone.split('/')[1].replace('_', ' ')}
          </span>
        )}
      </div>
      
      {dateInfo.secondary && (
        <div className="text-sm text-content-2 mt-1">
          Tu hora local: {dateInfo.secondary}
        </div>
      )}
      
      {dateInfo.diff && showTimeZoneInfo && (
        <div className="text-xs text-accent mt-1 flex items-center">
          <Info className="h-3 w-3 mr-1 inline-block" />
          {dateInfo.diff}
        </div>
      )}
    </div>
  );
};

/**
 * Versión específica para mostrar sólo la fecha (sin hora)
 */
export const DateDisplay: React.FC<Omit<DateTimeDisplayProps, 'format'>> = (props) => {
  return <DateTimeDisplay {...props} format="dateOnly" />;
};

/**
 * Versión específica para mostrar sólo la hora (sin fecha)
 */
export const TimeDisplay: React.FC<Omit<DateTimeDisplayProps, 'format'>> = (props) => {
  return <DateTimeDisplay {...props} format="timeOnly" />;
};

/**
 * Versión específica para mostrar tiempo relativo (hace X horas)
 */
export const TimeAgoDisplay: React.FC<Omit<DateTimeDisplayProps, 'format'>> = (props) => {
  return <DateTimeDisplay {...props} format="timeAgo" />;
};

export default DateTimeDisplay;